import { useEffect, useState } from "react";
import { useParams } from "wouter";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Seo from "@/components/Seo";
import { API_BASE } from "@/constants/api";
import { HELIO_SCORE_PILLARS } from "@/constants/helioScorePillars";
import { CONFIDENCE_NAME, TRADING_NAME } from "@/constants/company";

type Snapshot = {
  score?: {
    overall_score: number;
    helio_confidence: number | null;
    address?: string | null;
    pillars?: Record<string, { key: string; label?: string | null; score: number | null }>;
    illustrative_system?: {
      panel_kwp_low: number;
      panel_kwp_high: number;
      generation_kwh_yr_low: number;
      generation_kwh_yr_high: number;
      battery_kwh_low: number;
      battery_kwh_high: number;
      annual_benefit_gbp_low: number;
      annual_benefit_gbp_high: number;
    };
  };
};

export default function SharedReport() {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    void fetch(`${API_BASE}/api/widget/shares/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok || !body.success) {
          setError(body.error || "This report link is no longer available.");
          return;
        }
        setSnapshot(body.snapshot);
      })
      .catch(() => setError("Unable to load this report."));
  }, [token]);

  const score = snapshot?.score;
  const pillars = score?.pillars || {};

  return (
    <MarketingLayout>
      <Seo
        title={`Shared ${TRADING_NAME} Property Report`}
        description={`Read-only ${TRADING_NAME} Property Report shared from a partner listing.`}
        path={`/r/${token || ""}`}
      />
      <article className="mx-auto max-w-3xl px-4 py-16">
        <p className="text-sm uppercase tracking-wider text-[#29ffa9]/80 mb-3">Shared report</p>
        <h1 className="font-display text-4xl font-semibold tracking-tight mb-4">{TRADING_NAME} Property Report</h1>
        {error && <p className="text-red-400">{error}</p>}
        {score && (
          <>
            <p className="text-white/60 mb-2">{score.address}</p>
            <p className="text-5xl font-semibold tabular-nums mb-2">{score.overall_score}<span className="text-lg text-white/40">/100</span></p>
            <p className="text-white/55 mb-8">{CONFIDENCE_NAME} {score.helio_confidence ?? "Pending"}%</p>
            <div className="grid gap-3 sm:grid-cols-2 mb-8">
              {HELIO_SCORE_PILLARS.map((pillar) => {
                const row = pillars[pillar.key];
                return (
                  <div key={pillar.key} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex justify-between gap-2 mb-1">
                      <h2 className="font-medium">{pillar.name}</h2>
                      <span className="text-[#29ffa9] tabular-nums">
                        {row?.score == null ? "—" : `${row.score}/100`}
                      </span>
                    </div>
                    <p className="text-sm text-white/55">{pillar.summary}</p>
                  </div>
                );
              })}
            </div>
            {score.illustrative_system && (
              <section className="rounded-xl border border-white/10 p-4 mb-6">
                <h2 className="text-sm uppercase tracking-wider text-white/45 mb-2">Illustrative system scenario</h2>
                <p>
                  {score.illustrative_system.panel_kwp_low}–{score.illustrative_system.panel_kwp_high} kWp ·{" "}
                  {score.illustrative_system.generation_kwh_yr_low.toLocaleString()}–
                  {score.illustrative_system.generation_kwh_yr_high.toLocaleString()} kWh/yr · battery{" "}
                  {score.illustrative_system.battery_kwh_low}–{score.illustrative_system.battery_kwh_high} kWh
                </p>
                <p className="text-white/60 mt-2">
                  Potential annual benefit £{score.illustrative_system.annual_benefit_gbp_low.toLocaleString()}–£
                  {score.illustrative_system.annual_benefit_gbp_high.toLocaleString()}
                </p>
              </section>
            )}
            <p className="text-sm text-white/45">
              This is a read-only snapshot. Recipients are not added as leads unless they submit a form on {TRADING_NAME}.
            </p>
          </>
        )}
      </article>
    </MarketingLayout>
  );
}
